// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * VehiclesPage - Voertuigen & mobiliteit
 * Opslag via saveVehicles/loadVehicles (moduleKey 'vehicles', encrypted blob in db.wishes).
 */
import { useState, useEffect } from 'react'
import type { MutableRefObject } from 'react'
import { Plus, Trash2, Car, Truck, Bike, Caravan, Container } from 'lucide-react'
import { ModuleShell }     from '@/components/ui/ModuleShell'
import { InfoBlock }       from '@/components/ui/InfoBlock'
import { SaveStatusBadge } from '@/components/ui/SaveStatusBadge'
import { Input }           from '@/components/ui/Input'
import { Select }          from '@/components/ui/Select'
import { SelectWithOther } from '@/components/ui/SelectWithOther'
import { Textarea }        from '@/components/ui/Textarea'
import { Button }          from '@/components/ui/Button'
import { BackButton }      from '@/components/ui/BackButton'
import { ClosingGuidance } from '@/components/process/ClosingGuidance'
import { Card }            from '@/components/ui/Card'
import { LoadingState }    from '@/components/feedback/LoadingState'
import { useAuthStore }    from '@/store'
import { useAutosave }     from '@/hooks/useAutosave'
import { generateUUID }    from '@/lib/utils'
import { loadVehicles, saveVehicles } from '@/services/vehicles'
import {
  EMPTY_VEHICLE_ENTRY,
  VEHICLE_CATEGORIE_OPTIONS,
  VEHICLE_EIGENDOM_OPTIONS,
  VEHICLE_LOCATIE_OPTIONS,
  VEHICLE_AFHANDELING_OPTIONS,
  VEHICLE_LEASE_OPTIONS,
  VEHICLE_FINANCIER_OPTIONS,
  VEHICLE_MERK_OPTIONS,
  VEHICLE_MODEL_OPTIONS,
  VEHICLE_KLEUR_OPTIONS,
  VEHICLE_VERZEKERAAR_OPTIONS,
  VEHICLE_LEASE_MAATSCHAPPIJ_OPTIONS,
  type VehicleData,
  type VehicleEntry,
} from '@/types/vehicle'
import { AddressFields } from '@/components/ui/AddressFields'

/** Nederlands kenteken netjes formatteren (bijv. FZDT94 → FZ-DT-94). */
const PLATE_PATTERNS: [RegExp, string][] = [
  [/^([A-Z]{2})([A-Z]{2})(\d{2})$/, '$1-$2-$3'], // XX-XX-99
  [/^(\d{2})([A-Z]{2})([A-Z]{2})$/, '$1-$2-$3'], // 99-XX-XX
  [/^(\d{2})([A-Z]{2})(\d{2})$/, '$1-$2-$3'],    // 99-XX-99
  [/^([A-Z]{2})(\d{2})([A-Z]{2})$/, '$1-$2-$3'], // XX-99-XX
  [/^([A-Z]{2})(\d{2})(\d{2})$/, '$1-$2-$3'],    // XX-99-99
  [/^(\d{2})(\d{2})([A-Z]{2})$/, '$1-$2-$3'],    // 99-99-XX
  [/^(\d{1})([A-Z]{3})(\d{2})$/, '$1-$2-$3'],    // 9-XXX-99
  [/^(\d{2})([A-Z]{3})(\d{1})$/, '$1-$2-$3'],    // 99-XXX-9
  [/^([A-Z]{1})(\d{3})([A-Z]{2})$/, '$1-$2-$3'], // X-999-XX
  [/^([A-Z]{2})(\d{3})([A-Z]{1})$/, '$1-$2-$3'], // XX-999-X
  [/^([A-Z]{3})(\d{2})([A-Z]{1})$/, '$1-$2-$3'], // XXX-99-X
  [/^([A-Z]{1})(\d{2})([A-Z]{3})$/, '$1-$2-$3'], // X-99-XXX
  [/^(\d{1})([A-Z]{2})(\d{3})$/, '$1-$2-$3'],    // 9-XX-999
  [/^(\d{3})([A-Z]{2})(\d{1})$/, '$1-$2-$3'],    // 999-XX-9
]
function formatNLPlate(input: string): string {
  const s = input.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)
  for (const [re, rep] of PLATE_PATTERNS) if (re.test(s)) return s.replace(re, rep)
  return s
}

/** Logische jaarkeuzelijst; bestaande (afwijkende) waarde blijft zichtbaar. */
function buildYearOptions(current: string): { value: string; label: string }[] {
  const now = new Date().getFullYear()
  const years: { value: string; label: string }[] = []
  for (let y = now + 1; y >= 1950; y--) years.push({ value: String(y), label: String(y) })
  if (current && !years.some(o => o.value === current)) years.unshift({ value: current, label: current })
  return years
}

/** Lijnicoon per voertuigcategorie, zodat de soort direct herkenbaar is. */
function vehicleIcon(categorie: string, className = 'h-4 w-4') {
  switch (categorie) {
    case 'bedrijfswagen': return <Truck className={className} />
    case 'motor':
    case 'bromfiets':
    case 'scooter':       return <Bike className={className} />
    case 'camper':
    case 'caravan':       return <Caravan className={className} />
    case 'aanhangwagen':  return <Container className={className} />
    case 'personenauto':
    default:              return <Car className={className} />
  }
}

export function VehiclesPage({ saveRef }: { saveRef?: MutableRefObject<(() => Promise<void>) | null> } = {}) {
  const { userId } = useAuthStore()
  const [data, setData]   = useState<VehicleData>({ vehicles: [] })
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!userId) return
    loadVehicles(userId).then(d => {
      setData(d)
      setLoaded(true)
    })
  }, [userId])

  const { status, saveNow } = useAutosave({
    data,
    onSave: async (d) => { if (userId) await saveVehicles(userId, d) },
    enabled: loaded,
  })

  // Wizard-embed: registreer saveNow zodat de wizard pending invoer flusht vóór navigatie
  useEffect(() => {
    if (saveRef) saveRef.current = saveNow
    return () => { if (saveRef) saveRef.current = null }
  }, [saveRef, saveNow])

  function addVehicle() {
    const entry: VehicleEntry = { id: generateUUID(), ...EMPTY_VEHICLE_ENTRY }
    setData(prev => ({ vehicles: [...prev.vehicles, entry] }))
  }

  function removeVehicle(id: string) {
    setData(prev => ({ vehicles: prev.vehicles.filter(v => v.id !== id) }))
  }

  function update(id: string, field: keyof VehicleEntry, value: string) {
    setData(prev => ({
      vehicles: prev.vehicles.map(v => (v.id === id ? { ...v, [field]: value } : v)),
    }))
  }

  if (!loaded) return <LoadingState />

  const status_ = data.vehicles.length > 0 ? 'COMPLETE' : 'NOT_STARTED'

  return (
    <ModuleShell
      title="Voertuigen & mobiliteit"
      description="Auto, motor, camper of ander voertuig - leg vast wat uw nabestaanden moeten weten en regelen. Een paar minuten nu bespaart hen later veel uitzoekwerk."
      status={status_}
      actions={<SaveStatusBadge status={status} />}
    >
      <InfoBlock title="Goed om te weten - voertuigen" variant="blue" defaultOpen={false}>
        <p>Een auto lijkt eenvoudig, maar er hangt vaak meer aan dan u zou denken: een verzekering, wegenbelasting, soms een lopend contract. WilsApp legt uw wensen vast als persoonlijke leidraad; dit is geen officiële overdracht. Ligt er ergens een reservesleutel? Schrijf op waar. Noteer bij lease of financiering ook de naam en het telefoonnummer van de maatschappij, en een eventueel pechhulpabonnement. Zo'n contract stopt namelijk niet vanzelf; de termijnen lopen door tot iemand het overneemt of opzegt.</p>
        <p><strong>Tenaamstelling & vrijwaring:</strong> de daadwerkelijke overschrijving of vrijwaring van een voertuig verloopt via de <strong>RDW</strong> en kan pas na overlijden door de erfgenamen worden geregeld.</p>
        <p><strong>Verzekering:</strong> stel de verzekeraar tijdig op de hoogte; een voertuig moet verzekerd blijven zolang het op naam staat.</p>
      </InfoBlock>

      {data.vehicles.length === 0 && (
        <Card className="flex flex-col items-center gap-3 py-8 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sage-50 text-sage-600">
            <Car className="h-7 w-7" />
          </span>
          <div>
            <p className="text-sm font-medium text-ink">Nog geen voertuigen toegevoegd.</p>
            <p className="text-sm text-ink-3 mt-1 max-w-sm">
              Leg hier uw voertuigen vast zodat uw nabestaanden weten welke voertuigen aanwezig zijn.
            </p>
          </div>
          <Button onClick={addVehicle}>
            <Plus className="h-4 w-4" /> Voertuig toevoegen
          </Button>
        </Card>
      )}

      {data.vehicles.map((v, i) => (
        <Card key={v.id} className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2 text-ink font-medium min-w-0">
              <span className="flex-shrink-0 mt-0.5">{vehicleIcon(v.categorie)}</span>
              <div className="min-w-0">
                <span className="block truncate">{[v.kenteken, v.merk, v.typeModel].filter(Boolean).join(' - ') || `Voertuig ${i + 1}`}</span>
                {v.eigendomssituatie === 'financiering' && (v.financieringsmaatschappij ?? '') !== '' && (
                  <span className="block text-xs font-normal text-ink-3 truncate">Gefinancierd · {v.financieringsmaatschappij}</span>
                )}
              </div>
            </div>
            <Button variant="ghost" onClick={() => removeVehicle(v.id)} aria-label="Verwijderen" className="flex-shrink-0">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Identificatie */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Kenteken" value={v.kenteken}
              onChange={e => update(v.id, 'kenteken', e.target.value)}
              onBlur={e => update(v.id, 'kenteken', formatNLPlate(e.target.value))}
              placeholder="AB-123-C"
              autoCapitalize="characters" autoCorrect="off" autoComplete="off" spellCheck={false} />
            <Select label="Categorie" options={[...VEHICLE_CATEGORIE_OPTIONS]} placeholder="Kies categorie..."
              value={v.categorie} onChange={e => update(v.id, 'categorie', e.target.value)} />
            <SelectWithOther
              label="Merk"
              options={VEHICLE_MERK_OPTIONS}
              value={v.merk}
              onChange={val => update(v.id, 'merk', val)}
              placeholder="Kies een merk..."
              otherOptionLabel="Ander merk"
              otherFieldLabel="Merk"
              otherPlaceholder="Typ het merk"
            />
            {(VEHICLE_MODEL_OPTIONS[v.merk]?.length ?? 0) > 0 ? (
              <SelectWithOther
                label="Type / model"
                options={VEHICLE_MODEL_OPTIONS[v.merk]}
                value={v.typeModel}
                onChange={val => update(v.id, 'typeModel', val)}
                placeholder="Kies een model..."
                otherOptionLabel="Ander model"
                otherFieldLabel="Type / model"
                otherPlaceholder="Typ het model"
              />
            ) : (
              <Input label="Type / model" value={v.typeModel}
                onChange={e => update(v.id, 'typeModel', e.target.value)}
                placeholder="Typ het model" autoCapitalize="words" autoCorrect="off" />
            )}
            <Select label="Bouwjaar" options={buildYearOptions(v.bouwjaar)} placeholder="Kies bouwjaar..."
              value={v.bouwjaar} onChange={e => update(v.id, 'bouwjaar', e.target.value)} />
            <SelectWithOther
              label="Kleur"
              options={VEHICLE_KLEUR_OPTIONS}
              value={v.kleur}
              onChange={val => update(v.id, 'kleur', val)}
              placeholder="Kies een kleur..."
              otherOptionLabel="Andere kleur"
              otherFieldLabel="Kleur"
              otherPlaceholder="Typ de kleur"
            />
            <Input label="Chassisnummer" value={v.chassisnummer} onChange={e => update(v.id, 'chassisnummer', e.target.value)} />
            <Select label="Eigendomssituatie" options={[...VEHICLE_EIGENDOM_OPTIONS]} placeholder="Kies..."
              value={v.eigendomssituatie} onChange={e => update(v.id, 'eigendomssituatie', e.target.value)} />
            {v.eigendomssituatie === 'financiering' && (
              <SelectWithOther
                label="Bij welke financieringsmaatschappij?"
                options={VEHICLE_FINANCIER_OPTIONS}
                value={v.financieringsmaatschappij ?? ''}
                onChange={val => update(v.id, 'financieringsmaatschappij', val)}
                placeholder="Kies maatschappij..."
                otherOptionLabel="Andere maatschappij"
                otherFieldLabel="Naam financieringsmaatschappij"
                otherPlaceholder="Naam financieringsmaatschappij"
              />
            )}
            {v.eigendomssituatie === 'lease' && (
              <SelectWithOther
                label="Bij welke leasemaatschappij?"
                options={VEHICLE_LEASE_MAATSCHAPPIJ_OPTIONS}
                value={v.leaseMaatschappij ?? ''}
                onChange={val => update(v.id, 'leaseMaatschappij', val)}
                placeholder="Kies maatschappij..."
                otherOptionLabel="Andere maatschappij"
                otherFieldLabel="Naam leasemaatschappij"
                otherPlaceholder="Naam leasemaatschappij"
              />
            )}
          </div>

          {/* Locatie */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select label="Locatie" options={[...VEHICLE_LOCATIE_OPTIONS]} placeholder="Waar staat het voertuig?"
              value={v.locatieType} onChange={e => update(v.id, 'locatieType', e.target.value)} />
            <Input label="Land" value={v.locatieLand} onChange={e => update(v.id, 'locatieLand', e.target.value)} placeholder="Nederland" />
          </div>
          <AddressFields
            showAddition={false}
            values={{
              postalCode:  v.locatiePostcode,
              houseNumber: v.locatieHuisnummer ?? '',
              street:      v.locatieStraat,
              city:        v.locatiePlaats,
            }}
            onChange={(field, val) => {
              const map: Partial<Record<string, keyof VehicleEntry>> = {
                postalCode:  'locatiePostcode',
                houseNumber: 'locatieHuisnummer',
                street:      'locatieStraat',
                city:        'locatiePlaats',
              }
              const key = map[field]
              if (key) update(v.id, key, val)
            }}
            labels={{ street: 'Straat', city: 'Plaats' }}
          />

          {/* Afhandeling */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select label="Gewenste afhandeling" options={[...VEHICLE_AFHANDELING_OPTIONS]} placeholder="Wat moet ermee gebeuren?"
              value={v.gewensteAfhandeling} onChange={e => update(v.id, 'gewensteAfhandeling', e.target.value)} />
          </div>
          <Textarea label="Instructies" value={v.instructies} rows={2}
            onChange={e => update(v.id, 'instructies', e.target.value)} placeholder="Toelichting voor uw nabestaanden..." />

          {/* Documenten & sleutels */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Kentekencard (locatie)" value={v.kentekencard} onChange={e => update(v.id, 'kentekencard', e.target.value)} />
            <Input label="Tenaamstellingscode" value={v.tenaamstellingscode} onChange={e => update(v.id, 'tenaamstellingscode', e.target.value)} />
            <Input label="Reservesleutels" value={v.reservesleutels} onChange={e => update(v.id, 'reservesleutels', e.target.value)} />
            <Input label="Locatie documenten" value={v.documentLocatie} onChange={e => update(v.id, 'documentLocatie', e.target.value)} />
            <Input label="Notitie documenten" value={v.documentLocatieNotes} onChange={e => update(v.id, 'documentLocatieNotes', e.target.value)} />
            <Input label="Locatie sleutels" value={v.sleutelLocatie} onChange={e => update(v.id, 'sleutelLocatie', e.target.value)} />
            <Input label="Notitie sleutels" value={v.sleutelLocatieNotes} onChange={e => update(v.id, 'sleutelLocatieNotes', e.target.value)} />
          </div>

          {/* Verzekering */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SelectWithOther
              label="Verzekeraar"
              options={VEHICLE_VERZEKERAAR_OPTIONS}
              value={v.verzekeraar}
              onChange={val => update(v.id, 'verzekeraar', val)}
              placeholder="Kies verzekeraar..."
              otherOptionLabel="Andere verzekeraar"
              otherFieldLabel="Naam verzekeraar"
              otherPlaceholder="Naam verzekeraar"
            />
            <Input label="Polisnummer" value={v.polisnummer} onChange={e => update(v.id, 'polisnummer', e.target.value)} />
            <Input label="Contact verzekeraar" value={v.verzekeraarContact} onChange={e => update(v.id, 'verzekeraarContact', e.target.value)} />
          </div>

          {/* Lease */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select label="Lease?" options={[...VEHICLE_LEASE_OPTIONS]} placeholder="Kies..."
              value={v.heeftLease} onChange={e => update(v.id, 'heeftLease', e.target.value)} />
            {v.heeftLease === 'yes' && (
              <>
                <Input label="Leasemaatschappij" value={v.leaseMaatschappij} onChange={e => update(v.id, 'leaseMaatschappij', e.target.value)} />
                <Input label="Leasecontractnummer" value={v.leaseContractNr} onChange={e => update(v.id, 'leaseContractNr', e.target.value)} />
                <Input label="Contact lease" value={v.leaseContact} onChange={e => update(v.id, 'leaseContact', e.target.value)} />
              </>
            )}
          </div>
        </Card>
      ))}

      {data.vehicles.length > 0 && (
        <Button variant="secondary" onClick={addVehicle}>
          <Plus className="h-4 w-4" /> Voertuig toevoegen
        </Button>
      )}
      {data.vehicles.length > 0 && (
        <ClosingGuidance title="Uw voertuiggegevens zijn bijgewerkt">
          Werk deze gegevens bij wanneer u een voertuig koopt, verkoopt of wanneer uw situatie verandert.
        </ClosingGuidance>
      )}
      {!saveRef && <BackButton variant="bottom" primaryLabel="Klaar - terug naar dashboard" />}
    </ModuleShell>
  )
}
